import type {
  CreateMemberNoteRequest,
  DeleteMemberNoteRequest,
  FormattedMemberNote,
  FormattedMemberNotes,
  FormattedMemberNotesResponse,
  MemberNoteResponse,
  MemberNotesAPIRequest,
  MemberNotesResponse
} from '@library/types/members/memberNotes';
import { concat, mergeDeepWith } from 'ramda';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { defaultHeaders } from '@bo-constants/apiHeaders';
import { transformMemberNotes } from '@bo-utils/members/memberNotes';

const { member } = apiPaths;

export const memberNotesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMemberNotes: build.query<
      FormattedMemberNotesResponse | undefined,
      MemberNotesAPIRequest
    >({
      query: ({ memberId, page, perPage }) => ({
        url: `${member}/${memberId}/active_admin_comments`,
        method: 'GET',
        params: {
          page,
          per_page: perPage
        },
        headers: defaultHeaders
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (a: FormattedMemberNote, b: FormattedMemberNote) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as FormattedMemberNotes;
            }
            return b;
          },
          currentCache,
          newItems
        ) as FormattedMemberNotesResponse;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      transformResponse: (
        response: MemberNotesResponse,
        _,
        { page, perPage }
      ) => {
        return transformMemberNotes({ response, page, perPage });
      },
      providesTags: ['MemberNotes']
    }),
    createMemberNote: build.mutation<
      MemberNoteResponse,
      CreateMemberNoteRequest
    >({
      query: ({ memberId, noteDescription }) => ({
        url: `${member}/${memberId}/active_admin_comments`,
        method: 'POST',
        body: {
          body: noteDescription
        },
        headers: defaultHeaders
      }),
      invalidatesTags: ['MemberNotes']
    }),
    deleteMemberNote: build.mutation<void, DeleteMemberNoteRequest>({
      query: ({ id, memberId }) => ({
        url: `${member}/${memberId}/active_admin_comments/${id}`,
        method: 'DELETE',
        headers: defaultHeaders
      }),
      invalidatesTags: ['MemberNotes']
    })
  })
});

export const {
  useGetMemberNotesQuery,
  useCreateMemberNoteMutation,
  useDeleteMemberNoteMutation
} = memberNotesApi;
